import { Injectable } from '@angular/core';
import { interfaces } from '@leandredev/dcbm-common-lib';
import { BehaviorSubject, combineLatest, debounceTime, first, from, map, mergeMap, Observable, switchMap } from 'rxjs';

import { FileService } from 'src/app/services/file/file.service';
import { FileElm, FileInfo, IStorageStats } from 'src/app/utils/interfaces';

@Injectable({
  providedIn: 'root'
})
export class StoragesService {
  private _storageInfo = new BehaviorSubject<FileInfo[]>([]);
  public storageInfo = this._storageInfo.asObservable();

  private _fileRef = new BehaviorSubject<interfaces.IFile[]>([]);
  public fileRef = this._fileRef.asObservable();

  private _file = new BehaviorSubject<FileElm[]>([]);
  public file = this._file.asObservable();

  public storageStat = new StorageStats(this.file);

  constructor(private fileService: FileService) {}

  public refreshStorageInfo(): void {
    this.fileService
      .storageInfo()
      .pipe(
        map((info) => {
          this._storageInfo.next(info);
        })
      )
      .subscribe();

    this.fileService
      .fileRefs()
      .pipe(
        map((refs) => {
          this._fileRef.next(refs);
        })
      )
      .subscribe();

    combineLatest({ ref: this.fileRef, info: this.storageInfo })
      .pipe(
        debounceTime(200),
        map(({ ref, info }) => {
          const ids = new Set([...ref.map((e) => e._id), ...info.map((e) => e.id)]);

          const list: FileElm[] = this._file.value;

          while (list.length) list.pop();

          ids.forEach((id) => {
            const refElm = ref.find((e) => e._id === id);

            const infoElm = info.find((e) => e.id === id);

            const elm = Object.assign({ id, hasRef: Boolean(refElm), hasFile: Boolean(infoElm) }, refElm, infoElm);

            list.push(elm);
          });

          list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

          this._file.next(list);
        })
      )
      .subscribe();
  }

  public clearStorage(): void {
    this._fileRef.next([]);
    this._storageInfo.next([]);
  }

  public encrypt(file: FileElm): Observable<interfaces.IFile> {
    return this.fileService.addEncryptionToFile(file.id).pipe(
      map((_file) => {
        if (!file) return;
        file.name = _file.name;
        file.encrypted = true;

        return _file;
      })
    );
  }

  public download(fileId: string): Observable<{
    blob: Blob;
    filename: string | undefined;
  }> {
    return this.fileService.download(fileId);
  }

  public remove(fileId: string): Observable<void> {
    return this.fileService.removeFile(fileId).pipe(
      map((status) => {
        if (status) {
          const index = this._file.value.findIndex((e) => e.id === fileId);
          if (index) {
            this._file.value.splice(index, 1);
            this._file.next(this._file.value);
          }
        }
      })
    );
  }

  public removeTestData(name: string): Observable<void> {
    return this._file.pipe(
      first(),
      switchMap((files) => {
        const list = files.filter((elem) => elem.name === name.trim());

        return from(list).pipe(
          mergeMap((file) => {
            return this.remove(file.id).pipe(map(() => console.warn(file.id + ' ' + file.name + ' has ben removed')));
          }, 1)
        );
      })
    );
  }

  public createDescription(file: FileElm): Observable<unknown> {
    return this.fileService.createFileRef({ _id: file.id, ...file }).pipe(
      map((_file) => {
        if (!_file) return;
        file.hasRef = true;
      })
    );
  }
}

class StorageStats {
  constructor(private storageInfo: Observable<FileElm[]>) {}

  public value(): Observable<IStorageStats> {
    return this.storageInfo.pipe(
      map((value) => {
        const stats: IStorageStats = {
          files: 0,
          onDisk: 0,
          missing: 0,
          unRef: 0,
          useDisk: 0,
          onDiskEncrypted: 0
        };

        value.forEach((elem) => {
          stats.files += 1;
          if (elem.hasFile) {
            stats.onDisk += 1;
            stats.useDisk += elem.size;
            if (elem.encrypted) stats.onDiskEncrypted += 1;
          }
          if (!elem.hasFile) stats.missing += 1;

          if (!elem.hasRef) stats.unRef += 1;
        });

        return stats;
      })
    );
  }
}
